import React, { useEffect, useState } from 'react';
import { Sparkles, Plus, Trash2, RefreshCw, X, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select, Drawer } from '@/components/admin/shared';

const DEST_KIND_OPTS = [
  { value: 'channel',    label: 'Channel' },
  { value: 'product',    label: 'Product' },
  { value: 'article',    label: 'Article' },
  { value: 'audio_item', label: 'Audio (music/podcast)' },
  { value: 'video',      label: 'Video' },
  { value: 'collection', label: 'Collection' },
  { value: 'url',        label: 'Custom URL' },
];

function defaultForm() {
  return {
    name: '', enabled: true,
    trigger: 'time_on_page', trigger_threshold: 20,
    destination_kind: 'channel', destination_ref: '', destination_url: '',
    message_mode: 'default', message_default_key: 'continue_with', message_custom_text: '', cta_label: 'Continue',
    display_style: 'slide_up_card', priority: 100, frequency: 'once_per_session',
    audience_filter: [], content_type_filter: [], device_filter: [],
  };
}

export default function SuggestionRulesManager() {
  const [items, setItems] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const [{ data: list }, { data: c }] = await Promise.all([
        api.get('/admin/suggestion-rules'),
        api.get('/admin/suggestion-rules/config'),
      ]);
      setItems(list || []); setCfg(c);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Load failed'); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(defaultForm()); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...defaultForm(), ...it }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form,
        trigger_threshold: Number(form.trigger_threshold) || 0,
        priority: Number(form.priority) || 100,
      };
      if (editing) await api.put(`/admin/suggestion-rules/${editing.id}`, payload);
      else         await api.post('/admin/suggestion-rules', payload);
      setOpen(false);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!confirm(`Delete rule "${it.name}"?`)) return;
    try { await api.delete(`/admin/suggestion-rules/${it.id}`); await load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  const toggle = async (it) => {
    try { await api.put(`/admin/suggestion-rules/${it.id}`, { enabled: !it.enabled }); await load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Toggle failed'); }
  };

  if (!cfg) return <div className="p-4 text-xs font-mono text-[var(--pg-ink-faint)]">Loading Suggestion Engine…</div>;

  const triggerOpts = cfg.triggers.map((t) => ({ value: t, label: t.replace(/_/g, ' ') }));
  const styleOpts   = cfg.styles.map((s)   => ({ value: s, label: s.replace(/_/g, ' ') }));
  const freqOpts    = cfg.frequencies.map((f) => ({ value: f, label: f.replace(/_/g, ' ') }));

  return (
    <div className="space-y-4" data-testid="suggestion-rules-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Suggestion Engine</h3>
          <span className="text-[10px] font-mono tracking-widest uppercase text-[var(--pg-ink-faint)]">{items.length} rules</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2"><RefreshCw size={14} /> Refresh</button>
          <button data-testid="suggestion-rule-new" onClick={startNew} className="pg-btn-primary px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2"><Plus size={14} /> New rule</button>
        </div>
      </div>

      {msg && <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)] flex items-center gap-2"><Check size={14} className="text-[var(--pg-emerald-2)]" /> {msg}</div>}

      <div className="pg-admin-card rounded-[var(--pg-radius-md)] overflow-hidden">
        {items.length === 0 ? (
          <div className="p-6 text-xs font-mono text-[var(--pg-ink-faint)]">No suggestion rules yet. Create one to guide visitors after they engage with content.</div>
        ) : (
          <div className="divide-y divide-[var(--pg-border-2)]">
            {items.map((it) => (
              <div key={it.id} className="p-4 flex items-center gap-3" data-testid={`suggestion-row-${it.id}`}>
                <button onClick={() => toggle(it)} className="p-1" title={it.enabled ? 'Disable' : 'Enable'}>
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: it.enabled ? 'var(--pg-emerald-2)' : 'var(--pg-ink-faint)' }} />
                </button>
                <button onClick={() => startEdit(it)} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-base text-[var(--pg-ink)]">{it.name}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--pg-ink-muted)]">{it.display_style.replace(/_/g,' ')}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--pg-gold-2)]">{it.trigger.replace(/_/g,' ')} ≥ {it.trigger_threshold}</span>
                  </div>
                  <p className="text-[11px] font-mono text-[var(--pg-ink-muted)] mt-1">
                    → {it.destination_kind}{it.destination_ref ? `: ${it.destination_ref}` : ''}{it.destination_url ? ` · ${it.destination_url}` : ''} · priority {it.priority} · {it.frequency.replace(/_/g,' ')}
                  </p>
                </button>
                <button onClick={() => remove(it)} className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-neon-pink)]"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title={editing ? `Edit “${editing.name}”` : 'New Suggestion Rule'} onSave={save} saving={saving}>
        <TextField label="Rule name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="After scroll on articles → podcasts" />
        <Toggle label="Enabled" value={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Trigger" value={form.trigger} onChange={(v) => setForm({ ...form, trigger: v })} options={triggerOpts} />
          <TextField label="Threshold (seconds / % / count)" type="number" value={form.trigger_threshold} onChange={(v) => setForm({ ...form, trigger_threshold: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Destination kind" value={form.destination_kind} onChange={(v) => setForm({ ...form, destination_kind: v })} options={DEST_KIND_OPTS} />
          {form.destination_kind === 'url'
            ? <TextField label="Destination URL" value={form.destination_url} onChange={(v) => setForm({ ...form, destination_url: v })} placeholder="/vault" />
            : <TextField label="Destination ref (id or slug; leave blank for category landing)" value={form.destination_ref} onChange={(v) => setForm({ ...form, destination_ref: v })} />}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Message mode" value={form.message_mode} onChange={(v) => setForm({ ...form, message_mode: v })} options={[{value:'default',label:'Default'},{value:'custom',label:'Custom'}]} />
          <TextField label="CTA label" value={form.cta_label} onChange={(v) => setForm({ ...form, cta_label: v })} />
        </div>
        {form.message_mode === 'custom'
          ? <TextField label="Custom message" value={form.message_custom_text} onChange={(v) => setForm({ ...form, message_custom_text: v })} textarea rows={2} />
          : <Select label="Default message" value={form.message_default_key} onChange={(v) => setForm({ ...form, message_default_key: v })}
                    options={Object.entries(cfg.default_messages).map(([k, v]) => ({ value: k, label: v }))} />}
        <div className="grid grid-cols-3 gap-3">
          <Select label="Display style" value={form.display_style} onChange={(v) => setForm({ ...form, display_style: v })} options={styleOpts} />
          <Select label="Frequency" value={form.frequency} onChange={(v) => setForm({ ...form, frequency: v })} options={freqOpts} />
          <TextField label="Priority (lower first)" type="number" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} />
        </div>
      </Drawer>
    </div>
  );
}
